import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router';

import { Paper }          from '@material-ui/core';

import { makeStyles }     from '@material-ui/core/styles';

import { 
  default as useLocalStorageState,
}                         from 'use-local-storage-state';

import { 
  ResourceAction,
  ResourcesContext,
  ResourceDataTable,
  ResourceRef,
}                         from '../components/resource';

import {
  UserContext,
}                         from '../components/user';

import { 
  fmtIso8601AsLocalDateHrsMin,
  fmtTitleCase,
}                         from '../components/utils/formatters';

import { 
  getColumnsDisplayed,
  getEffectiveColumnDefs,
}                         from '../components/utils/helpers';


import { 
  fmtClient,
}                         from '../components/client';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

export default function Resources(props) {
  
  const { title, type, columnDefs, actions, filter } = props;  

  const effectiveTitle = title || (fmtTitleCase(type || 'Resource')+'s');
  const basePrefId = "resource_"+effectiveTitle.replace(/[^A-Z0-9]/ig, "_");
  const classes = useStyles();

  const [selected, setSelected] = React.useState(null);
  const [columnOrder, setColumnOrder] = useLocalStorageState(basePrefId+"_co", null);
  const [columnsDisplayed, setColumnsDisplayed] = useLocalStorageState(basePrefId+"_cd", null);

  const uctx = React.useContext(UserContext);

  const handlePrefsChange = (event) => {
    if(event.type==='COLUMN_ORDER') {
      setColumnOrder(event.newColumnOrder);
    } else if (event.type==='VIEW_COLUMNS') {
      let currentColumnsDisplayed=getColumnsDisplayed(baseColumnDefs,columnDefs,columnsDisplayed);
      if(event.action==='add') {
        currentColumnsDisplayed.push(event.changedColumn);
        setColumnsDisplayed(currentColumnsDisplayed);
      } else {
        setColumnsDisplayed(currentColumnsDisplayed.filter(e => e !== event.changedColumn));
      }
    }
  };
  
  const { resources: allResources, loading } = React.useContext(ResourcesContext);
  const resources = allResources.filter((resource) => {
    if (type && resource.ResourceType!==type) {
      return false;
    }
    if (filter && !filter(resource) ) {
      return false;
    }
    return true;
    //return (type?(resource.ResourceType===type):true);
  });

  const clientAccountAliases = new Set();
  resources.forEach((resource) => {
    clientAccountAliases.add(fmtClient(resource.ClientAccountId, uctx ));
  });
  
  const baseColumnDefs = [
    { name: 'ResourceId', label: 'Id', options: { 
      filter: false, 
      customBodyRenderLite: (dataIndex) => <ResourceRef 
        value={resources[dataIndex].ResourceId} 
        type={resources[dataIndex].ResourceType}
        show='id_only' copyable/> 
    }},
    { name: 'ResourceType', label: 'Type', options: { 
      customFilterListOptions: { render: v => `Type: ${v}` },
    }},
    { name: 'Description', label: 'Description', options: { 
      customFilterListOptions: { render: v => `Description: ${v}` },
      filterType: 'textField' 
    }},
    { name: 'ClientAccountId', label: 'Owner', options: { 
      customBodyRenderLite: (dataIndex) => fmtClient(resources[dataIndex].ClientAccountId, uctx ), 
      customFilterListOptions: { render: v => `Owner: ${v}` },
      filterType: 'multiselect',
      filterOptions: {
        names: Array.from(clientAccountAliases),
        logic: (location, filters, row) => {
          if (filters.length) return !filters.includes(fmtClient(location, uctx ));
          return false;
        },
      },
     }},
    { name: 'ResourceVersion', label: 'Version', 
      options: { 
        display: false,
        customFilterListOptions: { render: v => `Version: ${v}` },        
    }},
    { name: 'ResourceMigration', label: 'Migration', 
      options: { 
        display: false,
        customFilterListOptions: { render: v => `Migration: ${v}` },        
    }},
    { name: 'StackUpdated', label: 'Updated', 
      options: { 
        filter: false, 
        customBodyRender: fmtIso8601AsLocalDateHrsMin
    }},
    { name: 'Actions', label: ' ', 
      options: { 
        filter: false, 
        sort: false,
        viewColumns: false,
        customBodyRenderLite: (dataIndex) => <ResourceAction resource={resources[dataIndex]} size="small" actions={actions} />,
    }},
  ];

  const handleSelect = (resource) => {
    setSelected(resource);
  };

  if(!resources) {
    return(null);
  } 

  if(selected) {
    return <Redirect push to={"/Resources/"+selected.ResourceType+"s/"+selected.ResourceId} />;  
  }

  // during console development its not unusual for new columnDefs to be added/removed for a resource type
  // its important that the columnOrder array length matches the effective column defs array length
  // since stored column order preference may be stale we ignore it when definitions have changed
  // forcing user to rerrange columns from among the updated definitions again 
  const effectiveColumnDefs=getEffectiveColumnDefs(baseColumnDefs,columnDefs,columnsDisplayed);
  const effectiveColumnOrder=(columnOrder&&columnOrder.length===effectiveColumnDefs.length)?columnOrder:null;
  
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <ResourceDataTable
          title={effectiveTitle}
          data={resources}
          columns={effectiveColumnDefs}
          columnOrder={effectiveColumnOrder}
          loading={loading}
          onSelect={handleSelect}
          onChangePrefs={handlePrefsChange}
        />
      </Paper>
    </div>
  );
  
}

Resources.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  layout: PropTypes.oneOf(['list','card']),
  filter: PropTypes.func,
  columnDefs: PropTypes.array.isRequired,
  actions: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.func,
  ]).isRequired,  
};

Resources.defaultProps = {
  layout: 'list',
  columnDefs: [],
  actions: [],
};
